import { useMemo } from "react";
import { cn } from "@tellescope/ui";
import { formatDateTime, formatName, sanitizeZendeskHTMLContent } from "@tellescope/utils";
import { useTranslation } from "react-i18next";
import type { ThreadMessage } from "@tellescope/tanstack-query";
import { Clock } from "lucide-react";

interface ZendeskMessageProps {
  message: ThreadMessage;
  className?: string;
}

export const ZendeskMessage = ({ message, className }: ZendeskMessageProps) => {
  const { t } = useTranslation("inbox");

  const sanitizedContent = useMemo(() => sanitizeZendeskHTMLContent(message.content), [message.content]);

  const senderDisplay = useMemo(() => {
    if (!message.sender) return t("Unknown");

    const email = "email" in message.sender ? message.sender.email : undefined;
    const name = formatName(message.sender);

    return email || name || t("Unknown");
  }, [message.sender, t]);

  const timestampDisplay = useMemo(() => {
    const ts = message.isScheduled && message.sendAt ? message.sendAt : message.timestamp;
    return formatDateTime(ts, "MM/DD/YYYY at hh:mm a", "always");
  }, [message.isScheduled, message.sendAt, message.timestamp]);

  return (
    <div className={cn("border-t border-t-gray-200 pt-4", className)}>
      <div className="mb-3 space-y-1">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span className={cn(message.isOutbound ? "font-medium" : "")}>
            {senderDisplay}
            {message.isOutbound && <span className="ml-1 text-xs">({t("Agent")})</span>}
          </span>
          <div className="flex items-center gap-1.5">
            {message.isScheduled && (message.sendAt?.getTime() ?? 0) >= new Date().getTime() && (
              <Clock className="h-3 w-3" />
            )}
            <span>{timestampDisplay}</span>
          </div>
        </div>
        {message.error && <div className="text-xs text-destructive">{message.error}</div>}
        {message.isSending && <div className="text-xs text-muted-foreground">{t("Sending...")}</div>}
      </div>

      <div className="prose prose-sm max-w-none pb-4" dangerouslySetInnerHTML={{ __html: sanitizedContent }} />
    </div>
  );
};
